import { useState } from 'react';
import { Link } from 'react-router-dom';
import Input from '@/components/ui/Input';
import { searchService } from '@/services/api';
import { UserAvatar } from '@/components/ui/Avatar';
import { debounce } from '@/lib/utils';
import { ROUTES } from '@/constants';

export default function SearchPage() {
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState<any[]>([]);
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const handleSearch = async (q: string) => {
    if (!q.trim()) {
      setUsers([]);
      setPosts([]);
      return;
    }
    setLoading(true);
    const [uRes, pRes] = await Promise.all([
      searchService.searchUsers(q),
      searchService.searchPosts(q),
    ]);
    setUsers(uRes.data || []);
    setPosts(pRes.data || []);
    setLoading(false);
  };

  const debouncedSearch = debounce(handleSearch, 300);

  return (
    <div className="mx-auto max-w-2xl px-4 pt-4">
      <h1 className="mb-4 text-2xl font-bold">Explore</h1>

      <Input
        label="Search"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          debouncedSearch(e.target.value);
        }}
        placeholder="Search people, posts, hashtags"
      />

      {loading && (
        <div className="flex justify-center py-6">
          <div className="skeleton-text h-4 w-32" />
        </div>
      )}

      {!query && !loading && (
        <p className="py-12 text-center text-sm text-neutral-500 dark:text-neutral-400">
          Search for people and posts on KeLike.
        </p>
      )}

      {query && !loading && (
        <div className="mt-6 space-y-8">
          <section>
            <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
              People
            </h2>
            <div className="space-y-2">
              {users.length === 0 && (
                <p className="text-sm text-neutral-500 dark:text-neutral-400">No users found</p>
              )}
              {users.map((u) => (
                <Link
                  key={u.id}
                  to={ROUTES.PROFILE.replace(':username', u.username)}
                  className="flex items-center gap-3 rounded-xl bg-white p-3 transition-colors hover:bg-neutral-100 dark:bg-neutral-900 dark:hover:bg-neutral-800"
                >
                  <UserAvatar src={u.avatar_url} size="md" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-semibold">{u.display_name || u.username}</div>
                    <div className="truncate text-sm text-neutral-500 dark:text-neutral-400">@{u.username}</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          <section>
            <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
              Posts
            </h2>
            <div className="space-y-2">
              {posts.length === 0 && (
                <p className="text-sm text-neutral-500 dark:text-neutral-400">No posts found</p>
              )}
              {posts.map((p: any) => (
                <Link
                  key={p.id}
                  to={ROUTES.POST.replace(':id', p.id)}
                  className="block rounded-xl bg-white p-3 transition-colors hover:bg-neutral-100 dark:bg-neutral-900 dark:hover:bg-neutral-800"
                >
                  <div className="whitespace-pre-wrap break-words text-sm leading-relaxed">{p.content}</div>
                  <div className="mt-2 truncate text-xs text-neutral-500 dark:text-neutral-400">@{p.profiles?.username}</div>
                </Link>
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
